import React from 'react';
import {
  isShopstarGoLink, isTrialCta, trackGoClick, trackTrialCta,
} from '../../lib/track';

const cx = (...c) => c.filter(Boolean).join(' ');

// Default test id for tracked CTAs (overridable by the caller) so e2e/debug
// tests can target the conversion buttons.
const defaultTestId = (href) => {
  if (isShopstarGoLink(href)) return 'shopstar-go-cta';
  if (isTrialCta(href)) return 'trial-cta';
  return undefined;
};

const BASE = 'group inline-flex items-center justify-center gap-2 rounded-full font-bold leading-none text-center transition-all duration-200 ease-spring';

const VARIANTS = {
  primary: 'bg-amber text-ink shadow-pop hover:brightness-95 hover:-translate-y-0.5',
  secondary: 'bg-white text-ink border-2 border-line hover:border-ink hover:-translate-y-0.5',
  soft: 'bg-ink text-white hover:bg-ink/90 hover:-translate-y-0.5',
  ghost: 'text-ink hover:text-amber px-1',
};

const SIZES = {
  sm: 'text-sm px-4 py-2.5',
  md: 'text-base px-6 py-3.5',
  lg: 'text-base px-8 py-4',
};

/** Pill button. Renders <a> when `href` is set, otherwise <button>. */
const Button = ({
  href, children, variant = 'primary', size = 'md', className, icon, full, onClick, ...rest
}) => {
  const cls = cx(BASE, VARIANTS[variant], SIZES[size], full && 'w-full', className);
  // Fire GA4 shopstar_go_click for Shopstar Go links, or the begin_trial
  // conversion when a CTA leads into the sign-up flow, then run any
  // caller-supplied onClick. Go is checked first so a Go link is never counted
  // as a Classic trial. `children` doubles as the label (non-strings dropped).
  const handleClick = (e) => {
    if (href && isShopstarGoLink(href)) {
      trackGoClick(href, children);
    } else if (href && isTrialCta(href)) {
      trackTrialCta(href, children);
    }
    if (onClick) onClick(e);
  };
  const testId = rest['data-testid'] || defaultTestId(href);
  const common = { ...rest, className: cls, onClick: handleClick, 'data-testid': testId };
  const inner = (
    <>
      <span>{children}</span>
      {icon !== false && (
        <svg className="transition-transform duration-200 ease-spring group-hover:translate-x-1" width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </>
  );
  return href
    ? <a href={href} {...common}>{inner}</a>
    : <button type="button" {...common}>{inner}</button>;
};

export default Button;
